'use client';

import {
  alpha,
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';

const ROW_COUNT = 5;

/**
 * Mirrors ProfileActivityTab's own mobile-card / desktop-table split so the
 * skeleton doesn't show a desktop table shape on mobile (or vice versa).
 */
export const ProfileActivitySkeleton = () => {
  const theme = useTheme();
  const border = alpha(theme.palette.text.primary, 0.05);
  const track = alpha(theme.palette.text.primary, 0.05);

  return (
    <Box sx={{ position: 'relative', mt: 1 }}>
      {/* ── Mobile Card List (hidden on md+) ── */}
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          border: '1px solid',
          borderColor: border,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {[...Array(ROW_COUNT)].map((_, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              px: 2,
              py: 2,
              gap: 2,
              borderBottom: i < ROW_COUNT - 1 ? '1px solid' : 'none',
              borderColor: border,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
              <Skeleton
                variant="rectangular"
                width={44}
                height={44}
                sx={{ borderRadius: 1.5, bgcolor: track }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Skeleton variant="text" width={140} height={20} sx={{ bgcolor: track }} />
                <Skeleton variant="text" width={90} height={16} sx={{ bgcolor: track }} />
              </Box>
            </Box>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}
            >
              <Skeleton variant="text" width={70} height={20} sx={{ bgcolor: track }} />
              <Skeleton variant="text" width={50} height={16} sx={{ bgcolor: track }} />
            </Box>
          </Box>
        ))}
      </Box>

      {/* ── Desktop Table (hidden on xs) ── */}
      <TableContainer
        sx={{
          display: { xs: 'none', md: 'block' },
          border: '1px solid',
          borderColor: border,
          borderRadius: 2,
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ pt: 3, pb: 1 }}>
                <Skeleton variant="text" width={40} height={16} sx={{ bgcolor: track }} />
              </TableCell>
              <TableCell sx={{ pt: 3, pb: 1 }}>
                <Skeleton variant="text" width={60} height={16} sx={{ bgcolor: track }} />
              </TableCell>
              <TableCell align="right" sx={{ pt: 3, pb: 1, pr: 3 }}>
                <Skeleton variant="text" width={50} height={16} sx={{ bgcolor: track }} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...Array(ROW_COUNT)].map((_, i) => (
              <TableRow key={i}>
                <TableCell sx={{ py: 2.5, borderColor: border }}>
                  <Skeleton
                    variant="rounded"
                    width={56}
                    height={24}
                    sx={{ borderRadius: 1, bgcolor: track }}
                  />
                </TableCell>
                <TableCell sx={{ borderColor: border }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Skeleton
                      variant="rectangular"
                      width={44}
                      height={44}
                      sx={{ borderRadius: 1.5, bgcolor: track }}
                    />
                    <Box>
                      <Skeleton
                        variant="text"
                        width={160}
                        height={20}
                        sx={{ bgcolor: track, mb: 0.25 }}
                      />
                      <Skeleton variant="text" width={80} height={16} sx={{ bgcolor: track }} />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ pr: 3, borderColor: border }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 0.5,
                    }}
                  >
                    <Skeleton variant="text" width={70} height={20} sx={{ bgcolor: track }} />
                    <Skeleton variant="text" width={50} height={16} sx={{ bgcolor: track }} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
